import { hexToLinear, type LinearRGB } from '../../../core/color';
import type { Vec2, Vec3 } from '../../../geo/types';
import { layoutToWorld } from '../../../core/coords';
import type { ChunkSink } from '../../mesh/chunker';
import type { FaceMask, Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE, edgeIdFor, type SurfaceKind } from '../../mesh/attributes';
import { coneSolid, frustumSolid, orientedFace, planPoint } from '../solids';
import { HEAP_SEGMENTS, KETTLE_SEGMENTS, type YardBeams, type YardBlock, type YardItem, type YardKettle, type YardLogs } from './lastageYard';

export const YARD_EDGE_BASE = 52000;
/** Round timbers as hexagonal prisms: at yard distances six sides read as round. */
export const LOG_SEGMENTS = 6;

/**
 * The bare ground of the Lastage spit between the painted timber, Lab 40.6 2.1 29.3 (median of the
 * pixels lighter than L* 30 on the spit, museum scan 1873-2743 x 3546-4316): the heap's colour, as the
 * woodcut leaves its material unnamed.
 */
export const PAINTED_YARD_EARTH: LinearRGB = hexToLinear('#715d2f');

export interface YardColors {
  readonly timber: LinearRGB;
  readonly earth: LinearRGB;
  readonly iron: LinearRGB;
}

/** A kettle's legs: square iron bars, as thin as the woodcut's single strokes allow. */
const KETTLE_LEG_M = 0.08;

// Goods stand on the ground, so no floor is drawn under them.
const STANDING: FaceMask = { top: true, bottom: false, sides: true };
const CLOSED: FaceMask = { top: true, bottom: true, sides: true };

function style(color: LinearRGB, surface: SurfaceKind, index: number): VertexStyle {
  return { color, surface, edgeId: edgeIdFor(YARD_EDGE_BASE + index), strokeAngle: 0, extent: [0, 0] };
}

function frameAt(p: Vec2, yaw: number): Frame {
  return { origin: layoutToWorld(p, 0), yaw };
}

function block(builder: MeshBuilder, s: VertexStyle, item: YardBlock): void {
  const [l, w] = [item.length / 2, item.width / 2];
  builder.box(s, frameAt(item.at, item.yaw), [-l, 0, -w], [l, item.height, w], STANDING);
}

/* One log along u from u0 to u1, its axis at (v, y): a hexagonal prism with a corner down, so it
   rests on a corner and is exactly its diameter high. */
function log(builder: MeshBuilder, s: VertexStyle, frame: Frame, span: readonly [number, number], at: Vec2, d: number): void {
  const ring = Array.from({ length: LOG_SEGMENTS }, (_, i): Vec2 => {
    const a = Math.PI / 2 + (2 * Math.PI * i) / LOG_SEGMENTS;
    return [at[0] + (d / 2) * Math.cos(a), at[1] + (d / 2) * Math.sin(a)];
  });
  const [u0, u1] = span;
  ring.forEach((p, i) => {
    const q = ring[(i + 1) % LOG_SEGMENTS]!;
    const mid: Vec3 = [0, (p[1] + q[1]) / 2 - at[1], -((p[0] + q[0]) / 2 - at[0])];
    orientedFace(builder, s, frame, [planPoint(u0, p[1], p[0]), planPoint(u1, p[1], p[0]), planPoint(u1, q[1], q[0]), planPoint(u0, q[1], q[0])], mid);
  });
  orientedFace(builder, s, frame, ring.map((p) => planPoint(u0, p[1], p[0])), [-1, 0, 0]);
  orientedFace(builder, s, frame, ring.map((p) => planPoint(u1, p[1], p[0])), [1, 0, 0]);
}

function logStack(builder: MeshBuilder, s: VertexStyle, item: YardLogs): void {
  const frame = frameAt(item.at, item.yaw);
  const d = item.diameter;
  const end = item.tiers[0]!.length / 2;
  item.tiers.forEach((tier, k) => {
    for (let i = 0; i < tier.count; i++) {
      const v = (i - (tier.count - 1) / 2) * d;
      log(builder, s, frame, [end - tier.length, end], [v, d / 2 + k * d], d);
    }
  });
}

function beamHeap(builder: MeshBuilder, s: VertexStyle, item: YardBeams): void {
  const h = item.section / 2;
  for (const beam of item.beams) {
    const frame = frameAt([item.at[0] + beam.offset[0], item.at[1] + beam.offset[1]], beam.yaw);
    const l = beam.length / 2;
    builder.box(s, frame, [-l, beam.base, -h], [l, beam.base + item.section, h], beam.base > 0 ? CLOSED : STANDING);
  }
}

/* The pot as a closed round of KETTLE_SEGMENTS sides from the legs' height to its rim, on three legs
   set just inside its round. */
function kettle(builder: MeshBuilder, s: VertexStyle, item: YardKettle): void {
  const frame = frameAt(item.at, 0);
  const r = item.diameter / 2;
  const spec = { center: [0, 0] as Vec2, radius: r, segments: KETTLE_SEGMENTS, phase: 0 };
  frustumSolid(builder, s, frame, spec, item.legHeight, item.legHeight + item.potHeight, r, { top: true, bottom: true });
  const reach = r * Math.cos(Math.PI / KETTLE_SEGMENTS) - KETTLE_LEG_M;
  const h = KETTLE_LEG_M / 2;
  for (let i = 0; i < 3; i++) {
    const a = (2 * Math.PI * i) / 3;
    const leg: Frame = { origin: layoutToWorld([item.at[0] + reach * Math.cos(a), item.at[1] + reach * Math.sin(a)], 0), yaw: a };
    builder.box(s, leg, [-h, 0, -h], [h, item.legHeight, h], CLOSED);
  }
}

/** One yard item into a builder: timber, the heap in the painted earth, the kettle in iron. */
export function buildYardItem(builder: MeshBuilder, item: YardItem, colors: YardColors, index: number): void {
  const timber = style(colors.timber, SURFACE.wood, index);
  switch (item.kind) {
    case 'boxStack':
    case 'plankPile':
      block(builder, timber, item);
      return;
    case 'logStack':
      logStack(builder, timber, item);
      return;
    case 'beamHeap':
      beamHeap(builder, timber, item);
      return;
    case 'kettle':
      kettle(builder, style(colors.iron, SURFACE.wood, index), item);
      return;
    case 'heap': {
      const spec = { center: [0, 0] as Vec2, radius: item.diameter / 2, segments: HEAP_SEGMENTS, phase: 0 };
      coneSolid(builder, style(colors.earth, SURFACE.ground, index), frameAt(item.at, 0), spec, 0, item.height, false);
    }
  }
}

export function drawLastageYard(
  items: readonly YardItem[],
  colors: YardColors,
  chunks: ChunkSink
): { readonly items: number; readonly triangles: number } {
  let triangles = 0;
  items.forEach((item, index) => {
    const builder = chunks.builderAt('infrastructure', item.at);
    const before = builder.triangleCount;
    buildYardItem(builder, item, colors, index);
    triangles += builder.triangleCount - before;
  });
  return { items: items.length, triangles };
}
